/**
 * Что происходит после скана наклейки.
 *
 * Сканер отдаёт только токен — а дальше начинается собственно фича, и исходов
 * у неё четыре, а не один:
 *
 *   код привязан, Объект в области    → карточка с историей
 *   код привязан, Объекта в области нет → та же карточка, но только на чтение
 *   код свободен, есть право заводить → привязка: к существующей единице или к новой
 *   код свободен, права нет           → «обратитесь к прорабу»
 *
 * Логика вынесена сюда, а не в сканер, потому что тот же разбор нужен странице
 * `/e/:token` (вход с нативной камеры): там токен приходит из адреса, камера не
 * открывается вовсе — достаточно передать `initialToken`.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import Icon from './Icon';
import { track } from '../analytics';
import { backdropClose } from '../utils/modalBackdrop';
import EquipmentScanner from './EquipmentScanner';
import { formatCode } from '../utils/equipmentCode';
import EquipmentModal from './EquipmentModal';

/**
 * Простой модальный лист под сообщение или выбор.
 *
 * Объявлен на уровне модуля, а не внутри флоу: компонент, созданный внутри
 * другого компонента, пересоздаётся на каждом рендере и теряет состояние
 * вместе с фокусом в поле ввода.
 */
function Sheet({ title, onClose, children, footer, t: tProp }) {
  const { t: tHook } = useTranslation();
  const t = tProp || tHook;
  return (
    <div className="modal-overlay" {...backdropClose(onClose)}>
      <div className="modal" style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="scan" size={18} />
            {title}
          </h3>
          <button className="modal-close" onClick={onClose} aria-label={t('common.close')}>×</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

/**
 * «На этом коде раньше было…» — объяснение вместо пустоты.
 *
 * ⚠️ Показывается там, где возникает растерянность: человек сканирует
 * наклейку, которую сам приклеил, а система отвечает «код свободен». Так
 * бывает после того, как прораб отклонил его черновик: код вернулся в оборот,
 * а стикер остался на железе. Без этой строки происходящее выглядит как сбой.
 *
 * На уровне модуля, а не внутри флоу: компонент, объявленный внутри другого,
 * пересоздаётся на каждом рендере.
 */
function ReleasedNote({ released, t, loc }) {
  if (!released) return null;
  const date = released.at
    ? new Date(String(released.at).replace(' ', 'T') + 'Z').toLocaleDateString(loc, { day: '2-digit', month: 'short' })
    : null;
  return (
    <div style={{
      padding: '10px 12px', marginBottom: 12, borderRadius: 10,
      background: 'rgba(217,119,6,0.10)', border: '1px solid var(--orange)',
      fontSize: 13, lineHeight: 1.45,
    }}>
      {released.equipment_name
        ? t('equipment.tagReleasedFrom', { name: released.equipment_name })
        : t('equipment.tagReleased')}
      {(date || released.by_name) && (
        <div style={{ color: 'var(--text3)', fontSize: 12, marginTop: 3 }}>
          {[released.by_name, date].filter(Boolean).join(' · ')}
        </div>
      )}
    </div>
  );
}

/**
 * `lang` — необязательный проп для входа со страницы `/e/:token`.
 *
 * ⚠️ Язык там выбирается ДО скана, на странице ввода кода (`/e`), и должен
 * долетать до этого экрана, даже если сотрудник залогинен в обычном браузере
 * (а не в PWA) и попал сюда напрямую. Без override здесь молча подставлялся
 * бы язык профиля — то есть ровно то, от чего человек и уходил, явно выбирая
 * язык страницей раньше. На обычный вход через сканер в приложении (FAB,
 * Дашборд Работника) проп не передаётся — там языком остаётся язык профиля,
 * как и было.
 */
export default function EquipmentScanFlow({ initialToken, propertyId, onClose, onOpenTask, lang: langOverride }) {
  const { t: tHook, i18n } = useTranslation();
  const lang = (langOverride === 'en' || langOverride === 'ru')
    ? langOverride
    : (i18n.language === 'en' ? 'en' : 'ru');
  const t = langOverride ? i18n.getFixedT(lang) : tHook;
  const loc = lang === 'en' ? 'en-US' : 'ru-RU';

  const [token, setToken] = useState(initialToken || null);
  const [stage, setStage] = useState(initialToken ? 'resolving' : 'scan');
  const [res, setRes] = useState(null);
  const [error, setError] = useState(null);

  // Привязка свободного кода.
  const [bindProperty, setBindProperty] = useState(null);   // { id, name }
  const [candidates, setCandidates] = useState(null);       // null = грузим
  const [properties, setProperties] = useState(null);
  // Показывать полный список Объектов: сегодняшних Работ нет либо человек
  // сказал «другой Объект».
  const [needAll, setNeedAll] = useState(false);
  const [busy, setBusy] = useState(false);

  const [cardId, setCardId] = useState(null);
  const [restoring, setRestoring] = useState(false);

  /** Один и тот же разбор для скана камерой и для токена из адреса. */
  const apply = useCallback((r, tok) => {
    setToken(tok);
    setRes(r);
    // Единица в архиве: наклейка жива, единицы нет в парке — показываем это,
    // а не «код не найден», и даём вернуть, если есть право.
    if (r.state === 'archived') { setStage('archived'); return; }
    if (r.state === 'bound') {
      setCardId(r.equipment_id);
      setStage('card');
      track('equipment_card_view', { source: 'scan', in_scope: r.in_scope !== false });
      return;
    }
    if (!r.can_bind) { setStage('denied'); return; }

    // Мы уже внутри карточки Объекта — спрашивать, где стоит эта печь, незачем.
    if (propertyId) { setBindProperty({ id: propertyId }); setStage('bind'); return; }

    // ⚠️ Во всех остальных случаях Объект выбирается ЯВНО, отдельным шагом.
    // Раньше подставлялся Объект последней сегодняшней Работы — но выездов за
    // день бывает три, и «последний» угадывался в трети случаев. Молча
    // подставленный не тот Объект хуже отсутствия подсказки: человек жмёт
    // «Завести новое», не перечитывая шапку, и печь уезжает на чужой адрес.
    // Отдельный шаг стоит одного тапа и снимает целый класс тихих ошибок.
    setStage('property');
  }, [propertyId]);

  // Токен из адреса: камеру не открываем, резолвим сразу.
  useEffect(() => {
    if (!initialToken) return;
    let alive = true;
    api.equipmentResolveTag(initialToken, 'native_camera')
      .then((r) => {
        if (!alive) return;
        track('equipment_scan', { source: 'native_camera', state: r.state, in_scope: r.in_scope !== false });
        apply(r, initialToken);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e?.code === 'RATE_LIMITED' ? t('equipment.scanTooMany') : t('equipment.scanUnknown'));
        setStage('denied');
      });
    return () => { alive = false; };
  }, [initialToken, apply, t]);

  // Что уже есть на этом Объекте — чтобы наклейку можно было повесить на
  // заведённую единицу, а не плодить дубль.
  useEffect(() => {
    if (stage !== 'bind' || !bindProperty?.id) return;
    let alive = true;
    setCandidates(null);
    api.equipmentList(bindProperty.id, lang)
      .then((d) => { if (alive) setCandidates(d.applicable ? (d.items || []) : false); })
      .catch(() => { if (alive) setCandidates([]); });
    return () => { alive = false; };
  }, [stage, bindProperty?.id, lang]);

  // Полный список Объектов — только на шаге выбора и только если сегодняшних
  // Работ не нашлось («заехал по пути») либо человек попросил другой Объект.
  // На обходе список не нужен и стоит лишнего запроса на тонкой связи.
  useEffect(() => {
    if (stage !== 'property' || !needAll || properties) return;
    let alive = true;
    api.getProperties()
      .then((r) => { if (alive) setProperties(Array.isArray(r) ? r : (r?.items || [])); })
      .catch(() => { if (alive) setProperties([]); });
    return () => { alive = false; };
  }, [stage, needAll, properties]);

  const pickProperty = (p) => {
    track('equipment_tag_property_picked', { from_today: (res?.today_properties || []).some((x) => x.id === p.id) });
    setBindProperty(p);
    setStage('bind');
  };

  const bindExisting = async (equipmentId) => {
    setBusy(true);
    setError(null);
    try {
      await api.equipmentBindTag(token, equipmentId);
      track('equipment_tag_bind', { created_new: false });
      setCardId(equipmentId);
      setStage('card');
    } catch (e) {
      // 409 — код перехватил сосед по бригаде. Формулировка действием:
      // человек стоит у печи, ему нужно знать, что делать с этой наклейкой.
      setError(e?.code === 'TAG_TAKEN' ? t('equipment.tagTaken') : (e?.message || t('equipment.bindFailed')));
      setBusy(false);
    }
  };

  const afterCreate = (createdId) => {
    track('equipment_tag_bind', { created_new: true });
    // Без id показывать нечего — просто закрываемся. ⚠️ Решение принимается
    // здесь, в обработчике: вызов onClose прямо в теле рендера — побочный
    // эффект во время отрисовки, React на нём ругается и родитель успевает
    // отрисовать промежуточный кадр.
    if (!createdId) { onClose(); return; }
    setCardId(createdId);
    setStage('card');
  };

  if (stage === 'scan') {
    return <EquipmentScanner onResolved={apply} onClose={onClose} />;
  }

  if (stage === 'resolving') {
    return (
      <Sheet title={t('equipment.scanTitle')} onClose={onClose} t={t}>
        <div style={{ padding: '28px 0', textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>
          {t('equipment.scanResolving')}
        </div>
      </Sheet>
    );
  }

  if (stage === 'archived') {
    const restore = async () => {
      setRestoring(true);
      try {
        await api.equipmentRestore(res.equipment_id);
        track('equipment_restored', { equipment_id: res.equipment_id, source: 'scan' });
        setCardId(res.equipment_id);
        setStage('card');
      } catch (e) {
        setError(e?.message || t('equipment.restoreFailed'));
      } finally {
        setRestoring(false);
      }
    };
    return (
      <Sheet title={t('equipment.scanTitle')} onClose={onClose} t={t}
             footer={(
               <>
                 <button className="btn" onClick={onClose}>{t('common.close')}</button>
                 {res?.can_restore && (
                   <button className="btn btn-primary" data-ph-capture="equipment_restore_from_scan"
                           onClick={restore} disabled={restoring}>
                     {restoring ? t('common.saving') : t('equipment.restore')}
                   </button>
                 )}
               </>
             )}>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
          <span className="badge badge-neutral" style={{ marginRight: 6 }}>{t('equipment.viewArchive')}</span>
          {t('equipment.scanArchived', { name: res?.name })}
          {res?.reason && (
            <div style={{ marginTop: 8, padding: '6px 10px', background: 'var(--bg3)', borderRadius: 8, borderLeft: '2px solid var(--border2)' }}>
              {t('equipment.archiveReason')}: {res.reason}
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            {res?.can_restore ? t('equipment.scanArchivedHint') : t('equipment.scanArchivedNoRight')}
          </div>
          {error && <div style={{ marginTop: 8, color: 'var(--red)' }}>{error}</div>}
        </div>
        {token && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text3)', fontFamily: 'monospace' }}>
            {formatCode(token)}
          </div>
        )}
      </Sheet>
    );
  }

  if (stage === 'denied') {
    return (
      <Sheet title={t('equipment.scanTitle')} onClose={onClose} t={t}
             footer={<button className="btn" onClick={onClose}>{t('common.close')}</button>}>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.5 }}>
          {error || t('equipment.tagNotBoundNoRight')}
        </div>
        {token && (
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text3)', fontFamily: 'monospace' }}>
            {formatCode(token)}
          </div>
        )}
      </Sheet>
    );
  }

  // ── Шаг 1: на каком Объекте ────────────────────────────────────────────────
  if (stage === 'property') {
    const today = res?.today_properties || [];
    const showAll = needAll || today.length === 0;
    const list = showAll ? (properties || []) : today;

    return (
      <Sheet title={t('equipment.tagPickProperty')} onClose={onClose} t={t}>
        <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'monospace', marginBottom: 12 }}>
          {formatCode(token)}
        </div>

        <ReleasedNote released={res?.released} t={t} loc={loc} />

        {!showAll && (
          <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 8 }}>
            {t('equipment.tagTodayProperties')}
          </div>
        )}

        {showAll && properties === null ? (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>{t('common.loading')}</div>
        ) : list.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--text3)' }}>{t('equipment.tagNoProperties')}</div>
        ) : list.map((p) => (
          <div key={p.id} className="card clickable" style={{ padding: 12, marginBottom: 6, cursor: 'pointer' }}
               data-ph-capture="equipment_tag_property_pick"
               onClick={() => pickProperty(p)}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{p.name || p.address}</div>
            {p.name && p.address && (
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{p.address}</div>
            )}
          </div>
        ))}

        {/* «Заехал по пути» — сегодняшней Работы нет или она не на этом
            Объекте. Полный список тянем только по явной просьбе: на обходе он
            не нужен и стоит запроса на тонкой связи. */}
        {!showAll && (
          <button className="btn btn-sm" style={{ width: '100%', marginTop: 6 }}
                  data-ph-capture="equipment_tag_property_other"
                  onClick={() => setNeedAll(true)}>
            {t('equipment.tagOtherProperty')}
          </button>
        )}
      </Sheet>
    );
  }

  if (stage === 'new') {
    return (
      <EquipmentModal
        equipmentId={null}
        propertyId={bindProperty?.id}
        tagToken={token}
        onClose={() => setStage('bind')}
        onSaved={afterCreate}
        onOpenTask={onOpenTask}
      />
    );
  }

  if (stage === 'card') {
    if (!cardId) return null;
    return (
      <EquipmentModal
        key={cardId}
        equipmentId={cardId}
        // ⚠️ Передаём и снаружи, хотя карточка своим GET-запросом и так знает
        // property_id единицы. Подстраховка на случай глобального скана (FAB,
        // /e): у него своего Объекта нет и propertyId тут обычно undefined —
        // тогда карточка полагается на property_id из ответа сервера. Если он
        // когда-нибудь придёт пустым, «+ Новая работа» не должна откатываться
        // на полный список Объектов.
        propertyId={propertyId}
        // Код предъявляем вместе с запросом: без него карточка на Объекте вне
        // области данных отдаёт 404 — то есть ровно там, где сканирование и
        // нужнее всего. Отдаём ВСЕГДА, а не только вне области: по нему сервер
        // понимает, что карточку открыли сканом, и предлагает «Переместить
        // сюда» для переносной единицы (equipment_mobility_plan.md, фаза 2).
        // В своей области лишний параметр ничего не меняет — строгая проверка
        // доступа идёт первой.
        scanToken={token}
        onClose={onClose}
        onSaved={onClose}
        onOpenTask={onOpenTask}
      />
    );
  }

  // ── Привязка свободного кода ───────────────────────────────────────────────
  return (
    <Sheet
      title={t('equipment.tagFreeTitle')}
      onClose={onClose}
      t={t}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={busy}>{t('common.cancel')}</button>
          <button className="btn btn-primary" data-ph-capture="equipment_tag_create_new"
                  disabled={!bindProperty?.id || busy}
                  onClick={() => setStage('new')}>
            <Icon name="plus" size={14} /> {t('equipment.tagCreateNew')}
          </button>
        </>
      }
    >
      <div style={{ fontSize: 12, color: 'var(--text3)', fontFamily: 'monospace', marginBottom: 12 }}>
        {formatCode(token)}
      </div>

      <ReleasedNote released={res?.released} t={t} loc={loc} />

      {/* Объект уже выбран отдельным шагом — здесь его только показываем и
          даём вернуться. Своего селектора у этого экрана больше нет: два
          места выбора неизбежно разъедутся в правилах. */}
      {bindProperty?.name && (
        <div style={{ fontSize: 13, marginBottom: 12 }}>
          {t('equipment.tagProperty')}: <b>{bindProperty.name}</b>
          <button className="btn btn-sm" style={{ marginLeft: 8 }}
                  data-ph-capture="equipment_tag_property_change"
                  onClick={() => { setBindProperty(null); setCandidates(null); setStage('property'); }}>
            {t('equipment.tagChange')}
          </button>
        </div>
      )}

      {(
        <>
          {/* Жилой Объект: модуль там неприменим, и заводить единицу нельзя —
              бэкенд всё равно ответит 422. Говорим об этом до формы, а не после
              заполненной. */}
          {candidates === false ? (
            <div style={{ fontSize: 13, color: 'var(--text2)' }}>{t('equipment.notApplicable')}</div>
          ) : candidates === null ? (
            <div style={{ fontSize: 13, color: 'var(--text3)' }}>{t('common.loading')}</div>
          ) : candidates.length > 0 ? (
            <>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
                            letterSpacing: '.05em', marginBottom: 8 }}>
                {t('equipment.tagBindExisting')}
              </div>
              {candidates.map((e) => (
                <div key={e.id} className="card clickable" style={{ padding: 10, marginBottom: 6, cursor: 'pointer' }}
                     data-ph-capture="equipment_tag_bind_existing"
                     onClick={() => !busy && bindExisting(e.id)}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{e.name_tr || e.name}</div>
                  {(e.model || e.manufacturer) && (
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 2 }}>
                      {e.manufacturer ? `${e.manufacturer} ` : ''}{e.model}
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--text3)' }}>{t('equipment.tagNoCandidates')}</div>
          )}
        </>
      )}

      {error && <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 10 }}>{error}</div>}
    </Sheet>
  );
}
